import * as React from 'react';
import { useEffect, useState } from 'react';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import { Dialog } from './forms';

/* Subscribe to the team ICS feed: reads the existing link, creates one only on click. Revoking lives in Pipeline → Settings. */

const toWebcal = (url: string) => url.replace(/^https?/, 'webcal');
const toGoogle = (url: string) =>
    `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(toWebcal(url))}`;

export function SubscribeButton({ label = 'Subscribe' }: { label?: string }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button type="button" className="tb-btn" onClick={() => setOpen(true)}>
                {label}
            </button>
            <SubscribeDialog open={open} onClose={() => setOpen(false)} />
        </>
    );
}

export function SubscribeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const api = useAuthenticatedApi();
    /* null = loading; 'none' = no team feed yet (created only on click) */
    const [feed, setFeed] = useState<{ url?: string } | null | 'none' | 'failed'>(null);
    const [copied, setCopied] = useState<'yes' | 'failed' | null>(null);
    const [creating, setCreating] = useState(false);
    useEffect(() => {
        if (!open || (feed && feed !== 'failed')) return;
        api.get('/production/calendar-feeds')
            .then((r: any) => {
                const team = (Array.isArray(r.data) ? r.data : []).find((f: any) => f.subjectType === 'team' && f.url);
                setFeed(team ?? 'none');
            })
            .catch(() => setFeed('failed'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const create = async () => {
        setCreating(true);
        try {
            const r: any = await api.post('/production/calendar-feeds/team');
            setFeed(r.data?.url ? r.data : 'failed');
        } catch {
            setFeed('failed');
        } finally {
            setCreating(false);
        }
    };
    const url = feed && typeof feed === 'object' ? feed.url : undefined;
    const copy = async () => {
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopied('yes');
        } catch {
            setCopied('failed');
        }
        window.setTimeout(() => setCopied(null), 2000);
    };
    return (
        <Dialog
            open={open}
            title="Subscribe to the team calendar"
            onClose={onClose}
            footer={
                <button type="button" className="tb-btn" onClick={onClose}>
                    Done
                </button>
            }
        >
            <p className="pg-sub" style={{ margin: '0 0 14px' }}>
                Every production — and pending deals — shows up in your own calendar app and stays in sync.
            </p>
            {feed === null && <p className="pg-sub">Getting the feed link…</p>}
            {feed === 'failed' && <p className="pg-sub">Couldn’t get the feed link. Try again in a moment.</p>}
            {feed === 'none' && (
                <div className="feed-actions">
                    <button type="button" className="tb-new" onClick={create} disabled={creating}>
                        {creating ? 'Creating…' : 'Create feed link'}
                    </button>
                </div>
            )}
            {url && (
                <div className="feed-actions">
                    <a className="tb-new" href={toWebcal(url)}>
                        Add to Apple Calendar / Outlook
                    </a>
                    <a className="tb-btn" href={toGoogle(url)} target="_blank" rel="noreferrer">
                        Add to Google Calendar
                    </a>
                    <button type="button" className="tb-btn" onClick={copy}>
                        {copied === 'yes' ? 'Copied' : copied === 'failed' ? 'Couldn’t copy' : 'Copy feed link'}
                    </button>
                </div>
            )}
            <p className="pg-sub" style={{ margin: '14px 0 0', fontSize: 'var(--fs-xs)' }}>
                Anyone with the link can see the schedule. Manage and revoke links in Pipeline → Settings.
            </p>
        </Dialog>
    );
}
